import * as React from 'react';
import classNames from 'classnames';
import { Button } from '@msdyn365-commerce-modules/utilities';
import MilFilterRefiner from './mil-filter-refiner.component';
import { IGetFacetValueResponse, IFacetValuesEntity, ISearchResultsFacetsEntity } from '../../../actions/DataServiceEntities.g';
import { IMillProductsListPageConfig, IRefinersMappingData } from '../mill-products-list-page.props.autogenerated';
import { numericAwareValueCompare } from './utils';

export interface IMilRefineMenuProps {
    facets?: ISearchResultsFacetsEntity[] | undefined;
    searchTerm: string | undefined;
    initialSelectedFilters?: Record<string, string[]>;
    onChange?: (selectedFilters: Record<string, string[]>) => void;
    className?: string;
    actionContext: any;
    noFacetsFilterResultsText: string;
    config: IMillProductsListPageConfig;
}

const normalize = (s?: string) =>
    String(s ?? '')
        .trim()
        .toLowerCase();

const MilRefineMenu: React.FC<IMilRefineMenuProps> = ({
    facets = [],
    initialSelectedFilters = {},
    onChange,
    className,
    searchTerm,
    actionContext,
    noFacetsFilterResultsText,
    config
}) => {
    const {} = config ?? ({} as IMillProductsListPageConfig);

    const sortRefiners = config.sortRefiners || true;
    const sortRefinerValues = config.sortRefinerValues || true;
    const isContainRefinersMenu = config.isContainRefinersMenu || false;
    const minNumberOfFacetValuesForFilter = config.minNumberOfFacetValuesForFilter || 10;
    const refinersMapping = config.refinersMapping || [];

    const [expandedFacets, setExpandedFacets] = React.useState<Record<string, boolean>>({});
    const [selectedMap, setSelectedMap] = React.useState<Record<string, Set<string>>>({});
    const [facetValuesMap, setFacetValuesMap] = React.useState<Record<string, IFacetValuesEntity[]>>({});
    const [facetSearchText, setFacetSearchText] = React.useState<Record<string, string>>({});

    const mappingMap = React.useMemo(() => {
        const m = new Map<string, IRefinersMappingData>();
        (refinersMapping || []).forEach(item => {
            if (item?.originalRefinerName) {
                m.set(normalize(item.originalRefinerName), item);
            }
        });
        return m;
    }, [refinersMapping]);

    React.useEffect(() => {
        setExpandedFacets(prev => {
            const next: Record<string, boolean> = {};
            facets.forEach((f, i) => {
                const name = f?.Name ?? `facet-${i}`;
                next[name] = prev[name] ?? false;
            });
            return next;
        });

        setFacetValuesMap(prev => {
            const nextVals: Record<string, IFacetValuesEntity[]> = {};
            facets.forEach((f, i) => {
                const name = f?.Name ?? `facet-${i}`;
                nextVals[name] = (f?.Values ?? []).map(v => ({ ...v } as IFacetValuesEntity));
            });
            return nextVals;
        });
    }, [facets]);

    React.useEffect(() => {
        setFacetSearchText({});
    }, [initialSelectedFilters]);

    const selectedMapPlain: Record<string, string[]> = React.useMemo(() => {
        const r: Record<string, string[]> = {};
        Object.keys(selectedMap).forEach(k => {
            r[k] = Array.from(selectedMap[k]);
        });
        return r;
    }, [selectedMap]);

    React.useEffect(() => {
        const next: Record<string, Set<string>> = {};
        Object.keys(initialSelectedFilters || {}).forEach(k => {
            const arr = initialSelectedFilters[k] ?? [];
            next[k] = new Set(arr.map(String));
        });
        setSelectedMap(next);
    }, [initialSelectedFilters]);

    const toggleFacet = (facetName: string) => {
        setExpandedFacets(prev => ({ ...prev, [facetName]: !prev[facetName] }));
    };

    const emitSelectedRecord = (map: Record<string, Set<string>>) => {
        const result: Record<string, string[]> = {};
        Object.keys(map).forEach(k => {
            const arr = Array.from(map[k]);
            if (arr.length) {
                result[k] = arr;
            }
        });
        onChange?.(result);
    };

    const onValueToggle = (facetName: string, value: string) => {
        setSelectedMap(prev => {
            const next = { ...prev };
            const setForFacet = new Set(next[facetName] ? Array.from(next[facetName]) : []);
            if (setForFacet.has(value)) {
                setForFacet.delete(value);
            } else {
                setForFacet.add(value);
            }
            if (setForFacet.size > 0) {
                next[facetName] = setForFacet;
            } else {
                delete next[facetName];
            }
            emitSelectedRecord(next);
            return next;
        });
    };

    const handleFilterResults = React.useCallback(
        (facetName: string, resp?: IGetFacetValueResponse) => {
            let newVals: IFacetValuesEntity[];

            if (!resp) {
                newVals = facets.find(f => f.Name === facetName)?.Values ?? [];
            } else {
                newVals = (resp.Facets && resp.Facets.length > 0
                    ? resp.Facets.find(f => f.Name === facetName)?.Values ?? resp.Facets[0]?.Values ?? []
                    : []) as IFacetValuesEntity[];
            }

            setFacetValuesMap(prev => {
                const next = { ...prev };
                next[facetName] = newVals;
                return next;
            });
        },
        [facets]
    );

    const renderFacetValues = (facetName: string, values: IFacetValuesEntity[]) => {
        const filterText = facetSearchText[facetName] ?? '';

        return (
            <ul className='mil-refine-menu__values'>
                {values.length > 0 &&
                    values.map((val, i) => {
                        const v = val?.Value ?? '';
                        const checked = !!(selectedMap[facetName] && selectedMap[facetName].has(v));
                        return (
                            <li key={`${facetName}_${v}_${i}`}>
                                <label className='mil-refine-menu__value'>
                                    <input
                                        type='checkbox'
                                        checked={checked}
                                        onChange={() => onValueToggle(facetName, v)}
                                        aria-checked={checked}
                                    />
                                    <span className='mil-refine-menu__value-text'>{v} </span>
                                    <span className='mil-refine-menu__count'>({(val as any)?.HitsCount ?? 0})</span>
                                </label>
                            </li>
                        );
                    })}
                {values.length === 0 && filterText.trim() && <div className='mil-refine-menu__no-results'>{noFacetsFilterResultsText}</div>}
            </ul>
        );
    };

    const facetsToRender = React.useMemo(() => {
        const list = facets.slice();

        if (sortRefiners) {
            list.sort((a, b) => {
                const aName = a?.Name ?? '';
                const bName = b?.Name ?? '';

                const aFriendly = mappingMap.get(normalize(aName))?.friendlyRefinerName?.trim() || aName;
                const bFriendly = mappingMap.get(normalize(bName))?.friendlyRefinerName?.trim() || bName;

                return aFriendly.localeCompare(bFriendly);
            });
        }

        const prepared = list.map(f => {
            const name = f?.Name ?? '';
            const values = (facetValuesMap[name] ?? f?.Values ?? []).slice();
            if (sortRefinerValues) {
                values.sort(numericAwareValueCompare);
            }
            return { facet: f, values };
        });

        return prepared;
    }, [facets, facetValuesMap, sortRefiners, sortRefinerValues]);

    if (!facets || facets.length === 0) {
        return null;
    }

    return (
        <div className={classNames('mil-refine-menu', className, { 'mil-refine-menu--contained': isContainRefinersMenu })}>
            {facetsToRender.map(({ facet, values }, idx) => {
                const name = facet?.Name ?? `facet-${idx}`;
                const normalized = normalize(name);

                const mapping = mappingMap.get(normalized);
                if (mapping?.shouldHideRefiner === true) {
                    return null;
                }

                const displayName = (mapping?.friendlyRefinerName && String(mapping.friendlyRefinerName).trim()) || name;
                const isExpanded = expandedFacets[name] ?? false;
                const showFilterInput =
                    (values?.length || 0) >= (minNumberOfFacetValuesForFilter || 10) || (facetSearchText[name] ?? '') !== '';

                return (
                    <div className='mil-refine-submenu' key={name}>
                        <Button
                            className={classNames('mil-refine-menu__facet-header', {
                                'mil-refine-menu__facet-header--expanded': isExpanded,
                                'mil-refine-menu__facet-header--collapsed': !isExpanded
                            })}
                            onClick={() => toggleFacet(name)}
                            aria-expanded={isExpanded}
                        >
                            <span className='mil-refine-menu__facet-title'>{displayName}</span>
                        </Button>

                        {isExpanded && (
                            <>
                                {showFilterInput && (
                                    <MilFilterRefiner
                                        facetName={name}
                                        searchTerm={searchTerm}
                                        selectedRefiners={selectedMapPlain}
                                        actionContext={actionContext}
                                        externalText={facetSearchText[name] ?? ''}
                                        onResults={(resp?: IGetFacetValueResponse) => handleFilterResults(name, resp)}
                                        onSearchTextChange={(facetName, text) => {
                                            setFacetSearchText(prev => ({ ...prev, [facetName]: text }));
                                        }}
                                    />
                                )}

                                <div className='mil-refine-menu__facet-body'>{renderFacetValues(name, values)}</div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default MilRefineMenu;
